import React, { useState, useMemo, useEffect, useRef } from "react"
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout"
import { Head, router } from "@inertiajs/react"
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Plus, Search, MoreVertical, Edit, Trash2, Tag, X } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { useFilterStorage } from "@/hooks/use-filter-storage"

const defaultFilters = {
    searchTerm: "",
    statusFilter: "All Statuses",
};

export default function Categories({ categories }) {
    const { filters, updateFilter, resetFilters } = useFilterStorage('categories-filters', defaultFilters);

    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [isEditOpen, setIsEditOpen] = useState(false)
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [editingCategory, setEditingCategory] = useState(null)
    const [deletingCategory, setDeletingCategory] = useState(null)
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        active: 1,
    })
    const { toast } = useToast()

    // Handle URL category parameter from global search (open modal for specific category)
    const urlCategoryApplied = useRef(false);
    useEffect(() => {
        if (urlCategoryApplied.current) return;
        
        const urlParams = new URLSearchParams(window.location.search);
        const catId = urlParams.get('category');
        
        if (catId) {
            urlCategoryApplied.current = true;
            const category = categories.find(c => c.id === parseInt(catId));
            if (category) {
                // Open edit modal for this category
                setEditingCategory(category);
                setFormData({
                    name: category.name,
                    description: category.description || "",
                    active: category.active,
                });
                setIsEditOpen(true);
            }
        }
        
        // Handle ?action=new URL parameter to open create form from quick actions menu
        if (urlParams.get('action') === 'new') {
            setEditingCategory(null);
            setFormData({
                name: "",
                description: "",
                active: 1,
            });
            setIsCreateOpen(true);
            // Clean up URL
            router.replace(window.location.pathname, { preserveState: true, preserveScroll: true });
        }
    }, [categories]);

    // Filter categories based on search and status
    const filteredCategories = useMemo(() => {
        return categories.filter((category) => {
            const matchesSearch = category.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                                category.description?.toLowerCase().includes(filters.searchTerm.toLowerCase())
            const matchesStatus = filters.statusFilter === "All Statuses" || 
                                (filters.statusFilter === "Active" && category.active === 1) ||
                                (filters.statusFilter === "Inactive" && category.active === 0)
            return matchesSearch && matchesStatus
        })
    }, [categories, filters.searchTerm, filters.statusFilter])

    const handleCreate = (e) => {
        e.preventDefault()
        
        router.post("/categories", formData, {
            onSuccess: () => {
                setIsCreateOpen(false)
                setFormData({ name: "", description: "", active: 1 })
                toast({
                    title: "Success",
                    description: "Category created successfully.",
                })
            },
            onError: (errors) => {
                toast({
                    title: "Error",
                    description: "Failed to create category.",
                    variant: "destructive",
                })
            },
        })
    }

    const handleRowClick = (category) => {
        setEditingCategory(category)
        setFormData({
            name: category.name,
            description: category.description || "",
            active: category.active,
        })
        setIsEditOpen(true)
    }

    const handleEdit = (category) => {
        setEditingCategory(category)
        setFormData({
            name: category.name,
            description: category.description || "",
            active: category.active,
        })
        setIsEditOpen(true)
    }

    const handleUpdate = (e) => {
        e.preventDefault()
        
        router.put(`/categories/${editingCategory.id}`, formData, {
            onSuccess: () => {
                setIsEditOpen(false)
                setEditingCategory(null)
                setFormData({ name: "", description: "", active: 1 })
                toast({
                    title: "Success",
                    description: "Category updated successfully.",
                })
            },
            onError: (errors) => {
                toast({
                    title: "Error",
                    description: "Failed to update category.",
                    variant: "destructive",
                })
            },
        })
    }

    const handleDeleteClick = (category) => {
        setDeletingCategory(category)
        setIsDeleteOpen(true)
    }

    const handleDelete = () => {
        router.delete(`/categories/${deletingCategory.id}`, {
            onSuccess: () => {
                setIsDeleteOpen(false)
                setDeletingCategory(null)
                toast({
                    title: "Success",
                    description: "Category deleted successfully.",
                })
            },
            onError: (errors) => {
                toast({
                    title: "Error",
                    description: "Failed to delete category. It may have tasks assigned to it.",
                    variant: "destructive",
                })
            },
        })
    }

    const clearFilters = () => {
        resetFilters()
    }

    const hasActiveFilters = filters.searchTerm !== "" || filters.statusFilter !== "All Statuses"

    return (
        <AuthenticatedLayout>
            <Head title="DME | Categories" />
            
            <div className="flex flex-col gap-6 pb-8 pt-6 px-4 md:px-6 w-full">
                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-bold tracking-tight">Categories</h1>
                    <p className="text-muted-foreground">
                        Manage task categories and organize your workflow
                    </p>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-3">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <Input
                                placeholder="Search categories..."
                                value={filters.searchTerm}
                                onChange={(e) => updateFilter('searchTerm', e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        
                        <Select value={filters.statusFilter} onValueChange={(value) => updateFilter('statusFilter', value)}>
                            <SelectTrigger className="w-[140px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All Statuses">All Statuses</SelectItem>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                        
                        {hasActiveFilters && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={clearFilters}
                                className="h-9 px-3"
                            >
                                <X className="h-4 w-4 mr-1" />
                                Reset
                            </Button>
                        )}
                    </div>
                </div>

                {/* Results count */}
                <div className="text-sm text-muted-foreground">
                    {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'} found
                </div>

                <Card>
                    <CardContent className="p-0">
                        {filteredCategories.length === 0 ? (
                            <div className="text-center py-12">
                                <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="text-lg font-medium mb-2">
                                    {categories.length === 0 ? "No categories yet" : "No categories found"}
                                </h3>
                                <p className="text-muted-foreground mb-4">
                                    {categories.length === 0 
                                        ? "Get started by creating your first category."
                                        : "Try adjusting your search or filter criteria."
                                    }
                                </p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead className="w-12">ID</TableHead>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Tasks</TableHead>
                                        <TableHead className="w-12"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredCategories.map((category) => (
                                        <TableRow 
                                            key={category.id}
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => handleRowClick(category)}
                                        >
                                            <TableCell className="font-medium">
                                                {category.id}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center space-x-3">
                                                    <div className="flex-shrink-0">
                                                        <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 flex items-center justify-center">
                                                            <span className="text-white text-sm font-medium">
                                                                {category.name.charAt(0)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-sm">
                                                            {category.name}
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="max-w-xs">
                                                <div className="truncate text-sm text-muted-foreground">
                                                    {category.description || "—"}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        category.active === 1
                                                            ? "default"
                                                            : "secondary"
                                                    }
                                                    className={
                                                        category.active === 1
                                                            ? "bg-green-100 text-green-800 hover:bg-green-100"
                                                            : ""
                                                    }
                                                >
                                                    {category.active === 1 ? "Active" : "Inactive"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center text-sm text-muted-foreground">
                                                    <Tag className="h-4 w-4 mr-1" />
                                                    {category.tasks_count || 0}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 w-8 p-0"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <MoreVertical className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-48">
                                                        <DropdownMenuItem
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleEdit(category)
                                                            }}
                                                        >
                                                            <Edit className="h-4 w-4 mr-2" />
                                                            Edit Category
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleDeleteClick(category)
                                                            }}
                                                            className="text-destructive focus:text-destructive"
                                                        >
                                                            <Trash2 className="h-4 w-4 mr-2" />
                                                            Delete Category
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Create Dialog */}
                <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Create Category</DialogTitle>
                            <DialogDescription>
                                Add a new category to organize your tasks.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreate}>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Name</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) =>
                                            setFormData({ ...formData, name: e.target.value })
                                        }
                                        placeholder="e.g. Sales, Marketing, Support"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) =>
                                            setFormData({ ...formData, description: e.target.value })
                                        }
                                        placeholder="Category description (optional)"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="status">Status</Label>
                                    <Select
                                        value={formData.active.toString()}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, active: parseInt(value) })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="1">Active</SelectItem>
                                            <SelectItem value="0">Inactive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit">Create Category</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Edit Dialog */}
                <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Edit Category</DialogTitle>
                            <DialogDescription>
                                Update the category information.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleUpdate}>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-name">Name</Label>
                                    <Input
                                        id="edit-name"
                                        value={formData.name}
                                        onChange={(e) =>
                                            setFormData({ ...formData, name: e.target.value })
                                        }
                                        placeholder="Category name"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-description">Description</Label>
                                    <Textarea
                                        id="edit-description"
                                        value={formData.description}
                                        onChange={(e) =>
                                            setFormData({ ...formData, description: e.target.value })
                                        }
                                        placeholder="Category description (optional)"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-status">Status</Label>
                                    <Select
                                        value={formData.active.toString()}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, active: parseInt(value) })
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="1">Active</SelectItem>
                                            <SelectItem value="0">Inactive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit">Update Category</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete Category</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to delete "{deletingCategory?.name}"? 
                                This action cannot be undone. Tasks in this category 
                                will need to be reassigned.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDelete}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </AuthenticatedLayout>
    )
} 